import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {MixtureSchemaConfig} from 'constants/RegistryConstants';
import TagSchemaSettingsDropdown from 'settings/registry/TagSchemaSettingsDropdown';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InputChangeEvent} from 'util/Types';

interface Props {
  mixtureSchemaConfig: MixtureSchemaConfig;
  isWritable: boolean;
  handleChange: (newConfig: MixtureSchemaConfig) => void;
}

export function ComponentLotSelector(props: Props): JSX.Element {
  function updateComponentLotModes(
    componentLotTextEnabled: boolean,
    componentLotStorageEnabled: boolean
  ): void {
    props.handleChange({...props.mixtureSchemaConfig, componentLotTextEnabled, componentLotStorageEnabled});
  }

  function getOptionDisplayText(): string {
    if (
      props.mixtureSchemaConfig.componentLotStorageEnabled &&
      props.mixtureSchemaConfig.componentLotTextEnabled
    ) {
      return 'Both';
    }
    if (props.mixtureSchemaConfig.componentLotStorageEnabled) {
      return 'Inventory';
    }
    assert(
      Boolean(props.mixtureSchemaConfig.componentLotTextEnabled),
      'One of componentLotTextEnabled or componentLotStorageEnabled should always be true if allowMeasuredIngredients is true'
    );
    return 'Text';
  }

  const componentLotOptions = [
    {
      name: 'Text',
      description: 'Mixture ingredients can specify their component lot as a text value.',
      handleOnSelect: () => {
        updateComponentLotModes(true, false);
      },
    },
    {
      name: 'Inventory',
      description: 'Mixture ingredients can specify their component lot as a container and/or entity value.',
      handleOnSelect: () => {
        updateComponentLotModes(false, true);
      },
    },
    {
      name: 'Both',
      description: 'Mixture ingredients can specify their component lot in either format.',
      handleOnSelect: () => {
        updateComponentLotModes(true, true);
      },
    },
  ];
  return (
    <TagSchemaSettingsDropdown
      displayText={getOptionDisplayText()}
      isWritable={props.isWritable}
      options={componentLotOptions}
      label="Ingredient component lot format"
    />
  );
}

export default function MixtureSchemaConfiguration(props: Props): JSX.Element {
  const maybeRenderComponentLotSelector = (): JSX.Element | null => {
    if (props.mixtureSchemaConfig.allowMeasuredIngredients) {
      return <ComponentLotSelector {...props} />;
    }
    return null;
  };

  const renderMeasuredIngredientsCheckbox = (): JSX.Element => (
    <label className={css(styles.mixtureConfigOption)}>
      <input
        aria-labelledby="MixtureSchemaConfiguration-measuredIngredientsCheckbox"
        disabled={!props.isWritable}
        onChange={(e: InputChangeEvent) => {
          const newValue = e.target.checked;
          props.handleChange({
            allowMeasuredIngredients: newValue,
            // When allowMeasuredIngredients changes, we need to re-set the other two attributes accordingly
            componentLotTextEnabled: newValue ? true : null,
            componentLotStorageEnabled: newValue ? false : null,
          });
        }}
        type="checkbox"
        checked={props.mixtureSchemaConfig.allowMeasuredIngredients}
        {...getTestProps({component: 'MixtureSchemaConfiguration', element: 'measuredIngredientsCheckbox'})}
      />
      <span
        className={css(styles.mixtureConfigLabel)}
        id="MixtureSchemaConfiguration-measuredIngredientsCheckbox"
      >
        Allow recording measured values for ingredients
      </span>
    </label>
  );

  return (
    <div className={css(styles.mixtureConfigOptions)}>
      {renderMeasuredIngredientsCheckbox()}
      {maybeRenderComponentLotSelector()}
    </div>
  );
}

const styles = StyleSheet.create({
  mixtureConfigOptions: {
    marginTop: -10,
    marginBottom: 20,
  },
  mixtureConfigOption: {
    fontWeight: 'normal',
    marginBottom: 10,
  },
  mixtureConfigLabel: {
    marginLeft: 5,
  },
});
